import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { OnboardingLayout } from '../Layout';
import { useCountries } from '@/api/queries/reference';
import { usePassport, useUpsertPassport } from '@/api/queries/passport';
import { isCountryCode, isPassportNumber } from '@/utils/validators';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { CountryPicker } from '@/components/domain/CountryPicker';
import { PhotoUploader } from '@/components/domain/PhotoUploader';

export function OnboardingPassportPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const existing = usePassport();
  const upsert = useUpsertPassport();
  const pushToast = useUIStore((s) => s.pushToast);
  const { data: countries } = useCountries();

  const [nationality, setNationality] = useState(existing.data?.nationality_country_code ?? '');
  const [number, setNumber] = useState(existing.data?.passport_number ?? '');
  const [expiry, setExpiry] = useState(existing.data?.expiry_date ?? '');
  const [issue, setIssue] = useState(existing.data?.country_of_issue_code ?? '');
  // Object-key returned by /uploads/presign once the user picks a photo.
  // Initialised to null — if the user keeps an existing scan, the field is
  // left unset and the backend preserves the previous photo_object_key.
  const [photoKey, setPhotoKey] = useState<string | null>(null);

  const [pickerOpen, setPickerOpen] = useState<null | 'nat' | 'issue'>(null);

  const canSubmit =
    isCountryCode(nationality) && isPassportNumber(number) && expiry && isCountryCode(issue);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      await upsert.mutateAsync({
        nationality_country_code: nationality,
        passport_number: number,
        expiry_date: expiry,
        country_of_issue_code: issue,
        ...(photoKey ? { photo_object_key: photoKey } : {}),
      });
      navigate(paths.onboardingFillLater);
    } catch {
      pushToast({ body: t('errors.generic'), variant: 'error' });
    }
  };

  const countryName = (code: string) =>
    countries?.find((c) => c.code === code)?.name ?? code;

  return (
    <OnboardingLayout back title={t('onboarding.passport.title')}>
      <p style={{ color: 'var(--color-text-on-dark-muted)', marginTop: 0 }}>
        {t('onboarding.passport.subtitle')}
      </p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input
          label={t('onboarding.passport.nationality')}
          value={nationality ? countryName(nationality) : ''}
          placeholder="—"
          readOnly
          onClick={() => setPickerOpen('nat')}
        />
        <Input
          label={t('onboarding.passport.passport_number')}
          value={number}
          onChange={(e) => setNumber(e.target.value.toUpperCase())}
          autoCapitalize="characters"
        />
        <Input
          label={t('onboarding.passport.expiry')}
          type="month"
          value={expiry ? expiry.slice(0, 7) : ''}
          onChange={(e) => setExpiry(e.target.value ? `${e.target.value}-01` : '')}
        />
        <Input
          label={t('onboarding.passport.country_of_issue')}
          value={issue ? countryName(issue) : ''}
          placeholder="—"
          readOnly
          onClick={() => setPickerOpen('issue')}
        />

        <PhotoUploader
          purpose="passport_photo"
          existingUrl={existing.data?.photo_url ?? null}
          onUploaded={(key) => setPhotoKey(key)}
          label={t('onboarding.passport.photo')}
        />

        <Button
          type="submit"
          block
          size="lg"
          variant="inverted"
          disabled={!canSubmit}
          loading={upsert.isPending}
        >
          {t('common.complete')}
        </Button>
      </form>

      <BottomSheet
        open={pickerOpen !== null}
        onClose={() => setPickerOpen(null)}
        title={t(pickerOpen === 'issue' ? 'onboarding.passport.country_of_issue' : 'onboarding.passport.nationality')}
      >
        <CountryPicker
          value={pickerOpen === 'issue' ? issue : nationality}
          onPick={(code) => {
            if (pickerOpen === 'issue') setIssue(code);
            else setNationality(code);
            setPickerOpen(null);
          }}
        />
      </BottomSheet>
    </OnboardingLayout>
  );
}
